// src/hooks/useWebSocket.js
import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import * as C from '../constants';
import api from '../api';
import { getFileExtFromUrl } from '../node-parts/NodeCommons';

/**
 * WebSocket 实时监听后台任务状态，替代 5 秒轮询。
 *
 * ══════════════════════════════════════════════════════════════════
 * 📡 跨图板状态同步架构（EditorPage 端）
 *
 * 本 hook 在 EditorPage 中使用，同时加入两个 WebSocket 房间：
 *   - project-${pid}：当前正在编辑的图板
 *   - private-${userId}：用户级别房间（服务端 connection 时自动加入）
 *
 * 因此同一事件会收到 2 次（两个房间各一次），需要去重。
 *
 * 关键事件处理：
 *   1. node-updated：节点数据变更（loading/output/statusInfo 等），用于画布实时同步
 *   2. node-completed：节点生成完成，播放音效（去重防止播 2 次）
 *   3. project-status-changed：项目 pendingTasks 变化，更新右侧抽屉的状态指示器
 *      - 使用 pendingDelta（增量）而非绝对值，因为 WebSocket 始终在线不丢事件
 *      - DashboardPage 则用 pendingTasks 绝对值（见 DashboardPage.js 的注释）
 *   4. credits-updated：积分变化通知
 *
 * 相关文件：
 *   - server/routes/ai.js updateNodeInProject()：事件发射源
 *   - DashboardPage.js：桌面端的 project-status-changed 处理（用绝对值）
 *   - AssetDrawer.js：抽屉端的 ProjectStatusIndicator（消费 allProjects state）
 * ══════════════════════════════════════════════════════════════════
 *
 * @param {object} deps
 * @param {string}  deps.projectId
 * @param {Function} deps.setNodes
 * @param {Function} deps.setEdges
 * @param {Function} deps.setAlertMsg
 * @param {Function} deps.setAllProjects
 * @param {Function} deps.playCompletionSound
 * @param {Function} deps.playErrorSound
 * @param {Function} deps.cacheFileLocally
 * @param {object}   deps.crewIdRef
 * @param {object}   deps.crewNameRef
 * @param {object}   deps.skipNextAutoSaveRef
 * @param {object}   deps.projectVersionRef
 * @param {object}   deps.user
 * @param {Function} deps.refreshUser
 * @returns {{ socketRef: React.MutableRefObject }}
 */
const useWebSocket = (deps) => {
  const {
    projectId, setNodes, setEdges, setAlertMsg, setAllProjects,
    playCompletionSound, playErrorSound, cacheFileLocally,
    crewIdRef, crewNameRef, skipNextAutoSaveRef, projectVersionRef, isDataReadyRef,
    user, refreshUser,
  } = deps;

  const socketRef = useRef(null);
  // 去重：防止同一 node-completed 事件从 project 房间和 private 房间各收到一次导致播两次声音
  const recentCompletedRef = useRef(new Set());
  // 去重：防止同一 project-status-changed 事件收到两次导致 pendingDelta 叠加
  const recentStatusRef = useRef(new Set());

  useEffect(() => {
    if (!projectId) return;

    let isMounted = true;

    if (socketRef.current?.connected) {
      console.log('🔌 WebSocket 已存在连接，跳过重复创建');
      return;
    }

    if (socketRef.current) {
      socketRef.current.off('connect');
      socketRef.current.off('connect_error');
      socketRef.current.off('node-updated');
      socketRef.current.off('node-status');
      socketRef.current.off('project-status-changed');
      socketRef.current.off('disconnect');
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const wsToken = api.getToken ? api.getToken() : localStorage.getItem('cineart_token');

    const socket = io(C.API_BASE_URL, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      forceNew: false,
      auth: { token: wsToken },
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      if (!isMounted) return;
      console.log('🔌 WebSocket 已连接:', socket.id);
      socket.emit('join-project', projectId);

      // 🔧 重连后补偿：从数据库获取期间可能完成的生成结果
      // 解决：浏览器后台标签页/网络波动导致 WS 断连，错过 node-updated 推送
      api.get(`/api/projects/${projectId}`)
        .then(proj => {
          if (!isMounted) return;
          const dbNodes = proj.nodes || [];
          if (!Array.isArray(dbNodes) || dbNodes.length === 0) return;

          setNodes(currentNodes => {
            let changed = false;
            const updated = currentNodes.map(node => {
              const dbNode = dbNodes.find(n => n.id === node.id);
              if (!dbNode?.data) return node;

              // 先补偿“进行中状态”：前端可能因断线/刷新错过 node-status 推送
              if (node.data.loading && dbNode.data.loading && dbNode.data.statusInfo) {
                const localStatus = JSON.stringify(node.data.statusInfo || null);
                const dbStatus = JSON.stringify(dbNode.data.statusInfo || null);
                if (localStatus !== dbStatus) {
                  changed = true;
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      statusInfo: dbNode.data.statusInfo,
                      generationStartTime: dbNode.data.generationStartTime || node.data.generationStartTime,
                    }
                  };
                }
              }

              // 只补偿“前端还在 loading，但 DB 已完成”的节点
              if (node.data.loading && dbNode.data.loading === false && dbNode.data.output) {
                changed = true;
                console.log(`🔄 重连补偿: 节点 ${node.id} 已在 DB 中完成，同步结果`);
                return {
                  ...node,
                  data: {
                    ...node.data,
                    ...dbNode.data,
                    // 保留前端回调函数（DB 中没有）
                    onUpdate: node.data.onUpdate,
                    onDelete: node.data.onDelete,
                    onGenerate: node.data.onGenerate,
                    onDetach: node.data.onDetach,
                    onReupload: node.data.onReupload,
                    onReplace: node.data.onReplace,
                    onUploadAndReplace: node.data.onUploadAndReplace,
                    onRemoveRefFile: node.data.onRemoveRefFile,
                    onRemoveRefText: node.data.onRemoveRefText,
                    onRemoveRefAudio: node.data.onRemoveRefAudio,
                    onSlashAssetSpawn: node.data.onSlashAssetSpawn,
                    onHandleDoubleClick: node.data.onHandleDoubleClick,
                    onDisconnectDownstream: node.data.onDisconnectDownstream,
                    globalAssetsRef: node.data.globalAssetsRef,
                    publicAssetsRef: node.data.publicAssetsRef,
                    globalSpellsRef: node.data.globalSpellsRef,
                    localCacheUrl: undefined, // 新结果需重新缓存
                  }
                };
              }

              // 补偿“前端还在 loading，但 DB 已失败”的节点
              if (node.data.loading && dbNode.data.loading === false && dbNode.data.error) {
                changed = true;
                console.log(`🔄 重连补偿: 节点 ${node.id} 已在 DB 中失败`);
                return {
                  ...node,
                  data: {
                    ...node.data,
                    loading: false,
                    error: dbNode.data.error,
                    statusInfo: dbNode.data.statusInfo,
                    generationStartTime: null,
                  }
                };
              }

              return node;
            });
            if (changed) {
              skipNextAutoSaveRef.current = true;
              setAlertMsg({ type: 'success', text: '已同步期间完成的生成结果' });
            }
            return changed ? updated : currentNodes;
          });
        })
        .catch(err => console.warn('🔄 重连补偿查询失败:', err.message));
    });

    socket.on('connect_error', (error) => {
      if (!isMounted) return;
      console.warn('🔌 WebSocket 连接失败:', error.message);
    });

    // --- 节点更新 ---
    socket.on('node-updated', (payload) => {
      if (!isMounted) return;
      const { nodeId, data, version } = payload;

      // 同步服务端版本号，避免乐观并发控制冲突
      // 🛡️ 但若数据尚未就绪（Plan A 缓存秒开期间，nodes 还是过期快照），
      //   不允许 WS 把 projectVersionRef 从 -1 哨兵覆盖为真实 version。
      //   否则用户立刻 manual save 会用「过期 nodes + 真实 version」绕过 OCC 写库。
      if (version !== undefined && projectVersionRef?.current !== undefined) {
        if (isDataReadyRef && isDataReadyRef.current === false) {
          console.log('🛡️ WS: 数据未就绪，暂不同步 projectVersionRef');
        } else {
          projectVersionRef.current = version;
        }
      }

      skipNextAutoSaveRef.current = true;

      setNodes((currentNodes) => {
        const targetNode = currentNodes.find(n => n.id === nodeId);
        if (!targetNode) {
          console.log(`🗑️ 节点 ${nodeId} 已删除，忽略后台生成结果`);
          return currentNodes;
        }

        return currentNodes.map((node) => {
          if (node.id === nodeId) {
            const newData = { ...node.data, ...data };

            if (data.output && data.output !== node.data.output) {
              // 🔧 不删除 customSize：避免"删除 customSize → 50ms 后 View 恢复"的竞态窗口。
              // 如果用户在这 50ms 内离开项目，syncSave 会拍到没有 customSize 的快照写入 DB，
              // 导致下次进入项目时节点以默认 320×240 展示而非正确比例（如 9:16）。
              // 保留旧 customSize 作为过渡值（至少是合法尺寸），
              // ImageNodeView 通过 isNewImage 检测、VideoNodeView 通过 prevOutputRef 检测，
              // 都会在新媒体加载完成后正确重算 customSize 并保存。
              // 🔧 新图片到达时，清除旧的编辑器图层数据（annotationData）
              // 防止打开编辑器时还原旧图层，导致"多图层模式"困惑
              delete newData.annotationData;
              delete newData.annotationPreview;
              // 同时清除旧的本地缓存 URL（新图需要重新缓存）
              delete newData.localCacheUrl;
              console.log(`🔄 检测到新图片，已清除旧的 annotationData，节点将自动调整尺寸（保留旧 customSize 防止竞态丢失）`);
            }

            if (data.error) {
              console.error('❌ 节点生成失败，完整错误信息:', data.error);
              setAlertMsg({ type: 'error', text: `${node.data.title || '节点'} 生成失败:\n${data.error}` });
              playErrorSound();
            }

            if (data.output && !data.error && data.loading === false) {
              // 🆕 生成完成后自动重命名节点标题：模型名_序号.扩展名
              const modelName = node.data.modelName || '';
              if (modelName) {
                const defaultExts = { image: '.png', video: '.mp4', audio: '.mp3' };
                const ext = getFileExtFromUrl(data.output, defaultExts[node.data.type] || '');
                // 计算同模型名的节点数量作为序号
                const sameModelCount = currentNodes.filter(
                  n => n.data.modelName === modelName && n.id !== node.id && n.data.output
                ).length;
                const seq = String(sameModelCount + 1).padStart(3, '0');
                newData.title = `${modelName}_${seq}${ext}`;
              }

              setAlertMsg({ type: 'success', text: `${newData.title || node.data.title || '节点'} 生成完成` });

              const mediaType = node.data.type === 'video' ? 'video' : (node.data.type === 'audio' ? 'audio' : 'image');
              // 🆕 视频缓存延迟 20 秒：让浏览器的流式加载先获得完整带宽，
              // 避免 cacheFileLocally 的 fetch() 与浏览器下载同时竞争带宽，
              // 导致 onLoadedMetadata 迟迟不触发、黑屏长达数十秒。
              const cacheDelay = mediaType === 'video' ? 20000 : 0;
              setTimeout(() => {
              (async () => {
                try {
                  let cachedBlobUrl = null;
                  if (data.outputs && Array.isArray(data.outputs)) {
                    for (const url of data.outputs) {
                      const result = await cacheFileLocally(url, mediaType, crewIdRef.current, crewNameRef.current);
                      if (url === data.output && result?.url) cachedBlobUrl = result.url;
                    }
                  } else if (data.output) {
                    const result = await cacheFileLocally(data.output, mediaType, crewIdRef.current, crewNameRef.current);
                    if (result?.url) cachedBlobUrl = result.url;
                  }

                  if (cachedBlobUrl) {
                    setNodes(nds => nds.map(n => {
                      if (n.id === nodeId) {
                        return { ...n, data: { ...n.data, localCacheUrl: cachedBlobUrl } };
                      }
                      // 🔧 下游引用同步：缓存回写时也传播 localCacheUrl 到下游节点
                      // 背景：缓存完成后 localCacheUrl 更新为新的 blob URL，
                      //   下游 refFiles 中对应条目的 localCacheUrl 也需要同步更新，
                      //   否则 NodeConsole 仍展示旧/空的缩略图。
                      let hasChange = false;
                      let updData = { ...n.data };
                      if (updData.refFiles?.some(rf => rf.id === nodeId)) {
                        updData.refFiles = updData.refFiles.map(rf =>
                          rf.id === nodeId ? { ...rf, localCacheUrl: cachedBlobUrl } : rf
                        );
                        hasChange = true;
                      }
                      if (updData.refAudios?.some(ra => ra.id === nodeId)) {
                        updData.refAudios = updData.refAudios.map(ra =>
                          ra.id === nodeId ? { ...ra, localCacheUrl: cachedBlobUrl } : ra
                        );
                        hasChange = true;
                      }
                      return hasChange ? { ...n, data: updData } : n;
                    }));
                  }

                  console.log(`✅ 节点缓存完成: nodeId=${nodeId}, type=${node.data.type}`);
                } catch (err) {
                  console.error('📐 缓存失败:', err);
                }
              })();
              }, cacheDelay);
            }

            return { ...node, data: newData };
          }

          // ── 🔧 下游引用同步（修复：上游生成新图后下游 NodeConsole 不更新）──
          // 背景：WebSocket 推送的 node-updated 直接 setNodes，不走 handleUpdateNode，
          //   因此上游 output 变化时下游 refFiles/refAudios 里的旧 URL 不会被替换。
          //   结果是下游节点控制台仍显示旧的参考图缩略图。
          // 修复：遍历所有非目标节点，将引用了 nodeId 的 refFiles/refAudios 的 url 更新为新 output，
          //   同时清除旧的 localCacheUrl（防止 localCacheUrl || url 仍渲染旧缓存）。
          if (!(data.output && data.output !== targetNode.data.output)) return node;

          let hasRefChange = false;
          let syncData = { ...node.data };

          if (syncData.refFiles?.some(rf => rf.id === nodeId)) {
            syncData.refFiles = syncData.refFiles.map(rf => rf.id === nodeId
              ? { ...rf, url: data.output, localCacheUrl: undefined } : rf);
            hasRefChange = true;
          }
          if (syncData.refAudios?.some(ra => ra.id === nodeId)) {
            syncData.refAudios = syncData.refAudios.map(ra => ra.id === nodeId
              ? { ...ra, url: data.output, localCacheUrl: undefined } : ra);
            hasRefChange = true;
          }

          return hasRefChange ? { ...node, data: syncData } : node;
        });
      });

      setEdges((eds) =>
        eds.map((e) => {
          if (e.target === nodeId) {
            // 兜底：只要已经有 output / error，就强制停止连线传输动画。
            // 避免某些 Provider 先回填 output、后回填 loading=false 时出现"图片已出但线还在转"。
            const shouldTransmit = data.loading === true && !data.output && !data.error;
            return {
              ...e,
              data: { ...e.data, isTransmitting: shouldTransmit }
            };
          }
          return e;
        })
      );
    });

    // --- 节点状态 ---
    socket.on('node-status', (payload) => {
      if (!isMounted) return;
      const { nodeId, statusInfo } = payload;

      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id === nodeId) {
            const isFailedStatus =
              statusInfo?.phase === 'failed' ||
              ((statusInfo?.status === '失败' || statusInfo?.status === '错误') && !!statusInfo?.message);

            if (isFailedStatus) {
              const reason = statusInfo?.message || node.data.error || '生成失败';
              return {
                ...node,
                data: {
                  ...node.data,
                  statusInfo,
                  loading: false,
                  generationStartTime: null,
                  error: reason
                }
              };
            }

            return { ...node, data: { ...node.data, statusInfo } };
          }
          return node;
        })
      );
    });

    // ────────────────────────────────────────────────────────────────
    // 🔔 节点完成 → 播放音效（跨图板）
    //
    // 服务端同时发到 project-${pid} 和 private-${userId}，
    // EditorPage 在两个房间都会收到 → 必须去重，否则播放 2 次音效。
    // 去重 key = nodeId + timestamp，2s 后自动清理。
    //
    // ⚠️ 不要删除去重逻辑！
    // ────────────────────────────────────────────────────────────────
    socket.on('node-completed', (payload) => {
      if (!isMounted) return;
      const dedupKey = `${payload.nodeId}-${payload.timestamp}`;
      if (recentCompletedRef.current.has(dedupKey)) return;
      recentCompletedRef.current.add(dedupKey);
      // 2 秒后清理，避免 Set 无限增长
      setTimeout(() => recentCompletedRef.current.delete(dedupKey), 2000);

      playCompletionSound();
    });

    // --- 积分变化 ---
    socket.on('credits-updated', (payload) => {
      if (!isMounted) return;
      if (payload.userId === user?.id) {
        refreshUser();
      }
    });

    // ────────────────────────────────────────────────────────────────
    // 📡 项目状态变化 → 更新 EditorPage 右侧抽屉的各图板状态指示器
    //
    // 这里使用 pendingDelta（增量），而非 DashboardPage 使用的 pendingTasks（绝对值）。
    // 原因：EditorPage 进入时通过 API 获取了精确的 allProjects 初始值，
    //       且 WebSocket 始终在线（同时在 project 和 private 两个房间），不存在丢事件的问题。
    //       增量更新更轻量。
    //
    // 去重：服务端同时发到 project-${pid} 和 private-${userId} 两个房间，
    //       EditorPage 同时在两个房间，会收到 2 次相同事件。
    //       使用 Set<projectId-timestamp> 去重，2s 后自动清理。
    //       如果不去重，pendingDelta 会被叠加两次（+1 变成 +2），显示错误。
    //
    // ⚠️ 修改注意事项：
    //   - 不要把这里改为使用 pendingTasks 绝对值（可以但没必要，增量更准确且不会覆盖其他并发更新）
    //   - 不要删除去重逻辑，否则 delta 会被重复计算
    //   - DashboardPage 的 project-status-changed 处理在 DashboardPage.js 中（用绝对值，不去重）
    // ────────────────────────────────────────────────────────────────
    socket.on('project-status-changed', (payload) => {
      if (!isMounted) return;
      // 去重：同一事件从 project 房间和 private 房间各收到一次
      const dedupKey = `${payload.projectId}-${payload.timestamp}`;
      if (recentStatusRef.current.has(dedupKey)) return;
      recentStatusRef.current.add(dedupKey);
      setTimeout(() => recentStatusRef.current.delete(dedupKey), 2000);

      const { projectId: changedProjectId, pendingDelta, hasUnseenCompleted } = payload;
      const numProjectId = typeof changedProjectId === 'string' ? parseInt(changedProjectId) : changedProjectId;

      setAllProjects(prev => prev.map(p => {
        if (p.id !== numProjectId) return p;
        const updates = {};
        if (pendingDelta !== undefined && pendingDelta !== 0) {
          updates.pendingTasks = Math.max(0, (p.pendingTasks || 0) + pendingDelta);
        }
        if (hasUnseenCompleted !== undefined) {
          updates.hasUnseenCompleted = hasUnseenCompleted;
        }
        return { ...p, ...updates };
      }));
    });

    socket.on('disconnect', (reason) => {
      if (!isMounted) return;
      console.log('🔌 WebSocket 已断开:', reason);
    });

    // 🔧 Page Visibility 处理：标签页回到前台时主动重连
    // 解决：Chrome/Edge 后台标签页节流 WebSocket，导致丢失推送
    const handleVisibilityChange = () => {
      if (!isMounted) return;
      if (!document.hidden && socket && !socket.connected) {
        console.log('🔌 标签页回到前台，主动重连 WebSocket');
        socket.connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 🛡️ 心跳轮询保护：每 30 秒检查一次是否有 loading 节点在 DB 中已完成
    // 兜底：即使 WebSocket 推送全部丢失，也能在 30s 内恢复结果
    // 🛡️ 同时做本地超时检测：generationStartTime > 30 分钟 → 直接标记超时
    const STALE_LOADING_THRESHOLD_MS = 30 * 60 * 1000; // 30 分钟
    const heartbeatInterval = setInterval(() => {
      if (!isMounted) return;
      // 使用 setNodes 的函数式更新来读取最新节点
      setNodes(currentNodes => {
        // ── 第一层：本地超时检测（无需网络请求）──
        const now = Date.now();
        let staleChanged = false;
        const afterStaleCheck = currentNodes.map(node => {
          if (node.data?.loading && node.data?.generationStartTime) {
            const age = now - node.data.generationStartTime;
            if (age > STALE_LOADING_THRESHOLD_MS) {
              staleChanged = true;
              console.warn(`🛡️ 前端超时清理: 节点 ${node.id} 已 loading ${Math.round(age / 60000)} 分钟，标记超时`);
              return {
                ...node,
                data: {
                  ...node.data,
                  loading: false,
                  error: '生成超时（超过 30 分钟），请重新生成',
                  statusInfo: null,
                  generationStartTime: null,
                }
              };
            }
          }
          return node;
        });
        if (staleChanged) {
          skipNextAutoSaveRef.current = true;
          // 返回清理后的节点，同时触发一次保存以同步到 DB
          return afterStaleCheck;
        }

        // ── 第二层：DB 心跳补偿（网络请求）──
        const loadingNodes = afterStaleCheck.filter(n => n.data?.loading === true);
        if (loadingNodes.length === 0) return currentNodes; // 无 loading 节点，跳过

        // 有 loading 节点，查询 DB 最新状态
        api.get(`/api/projects/${projectId}`)
          .then(proj => {
            if (!isMounted) return;
            const dbNodes = proj.nodes || [];
            if (!Array.isArray(dbNodes)) return;

            setNodes(prevNodes => {
              let changed = false;
              const updated = prevNodes.map(node => {
                if (!node.data?.loading) return node;
                const dbNode = dbNodes.find(n => n.id === node.id);
                if (!dbNode?.data) return node;

                if (dbNode.data.loading === false && (dbNode.data.output || dbNode.data.error)) {
                  changed = true;
                  const isSuccess = !!dbNode.data.output;
                  console.log(`🛡️ 心跳补偿: 节点 ${node.id} 已在 DB 中${isSuccess ? '完成' : '失败'}`);
                  if (isSuccess) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        ...dbNode.data,
                        onUpdate: node.data.onUpdate,
                        onDelete: node.data.onDelete,
                        onGenerate: node.data.onGenerate,
                        onDetach: node.data.onDetach,
                        onReupload: node.data.onReupload,
                        onReplace: node.data.onReplace,
                        onUploadAndReplace: node.data.onUploadAndReplace,
                        onRemoveRefFile: node.data.onRemoveRefFile,
                        onRemoveRefText: node.data.onRemoveRefText,
                        onRemoveRefAudio: node.data.onRemoveRefAudio,
                        onSlashAssetSpawn: node.data.onSlashAssetSpawn,
                        onHandleDoubleClick: node.data.onHandleDoubleClick,
                        onDisconnectDownstream: node.data.onDisconnectDownstream,
                        globalAssetsRef: node.data.globalAssetsRef,
                        publicAssetsRef: node.data.publicAssetsRef,
                        globalSpellsRef: node.data.globalSpellsRef,
                        localCacheUrl: undefined,
                      }
                    };
                  } else {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        loading: false,
                        error: dbNode.data.error,
                        statusInfo: dbNode.data.statusInfo,
                        generationStartTime: null,
                      }
                    };
                  }
                }

                // 进行中状态补偿：确保刷新/断连后仍能看到最新进度文字/百分比
                if (dbNode.data.loading === true && dbNode.data.statusInfo) {
                  const localStatus = JSON.stringify(node.data.statusInfo || null);
                  const dbStatus = JSON.stringify(dbNode.data.statusInfo || null);
                  if (localStatus !== dbStatus) {
                    changed = true;
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        statusInfo: dbNode.data.statusInfo,
                        generationStartTime: dbNode.data.generationStartTime || node.data.generationStartTime,
                      }
                    };
                  }
                }
                return node;
              });
              if (changed) {
                skipNextAutoSaveRef.current = true;
                setAlertMsg({ type: 'success', text: '已同步完成的生成结果' });
              }
              return changed ? updated : prevNodes;
            });
          })
          .catch(() => {}); // 静默失败，下次心跳再试

        return currentNodes; // 外层 setNodes 不修改
      });
    }, 30000);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(heartbeatInterval);
      if (socketRef.current === socket) {
        socket.off('connect');
        socket.off('connect_error');
        socket.off('node-updated');
        socket.off('node-status');
        socket.off('credits-updated');
        socket.off('project-status-changed');
        socket.off('disconnect');
        socket.emit('leave-project', projectId);
        socket.disconnect();
        socketRef.current = null;
        // 🛡️ M5: 清理去重 Set，释放残留条目引用
        recentCompletedRef.current.clear();
        recentStatusRef.current.clear();
        console.log('🔌 WebSocket 清理完成');
      }
    };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { socketRef };
};

export default useWebSocket;
